/**
 * API 客户端 — 全局 Axios 实例
 * @ref: master.md § 2 System Architecture / Security
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { unwrapApiPayload } from './payload';
import type { ApiClientError } from './apiClientError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

interface RetriableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface RefreshTokenPayload {
  accessToken?: string;
  refreshToken?: string;
}

interface ErrorPayload {
  message?: string;
  msg?: string;
}

let refreshTokenRequest: Promise<string> | null = null;

const clearAuthAndRedirect = () => {
  localStorage.removeItem('uvp_access_token');
  localStorage.removeItem('uvp_refresh_token');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const getRequestUrl = (config?: AxiosRequestConfig) => config?.url ?? '';

const shouldAttemptTokenRefresh = (config?: AxiosRequestConfig) => {
  const url = getRequestUrl(config);
  return !url.includes('/auth/login') && !url.includes('/auth/refresh');
};

const refreshAccessToken = () => {
  if (!refreshTokenRequest) {
    const refreshToken = localStorage.getItem('uvp_refresh_token');
    if (!refreshToken) {
      return Promise.reject(new Error('登录已过期，请重新登录'));
    }

    refreshTokenRequest = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { timeout: 30_000 })
      .then(res => {
        const payload: RefreshTokenPayload = res.data?.data ?? res.data ?? {};
        if (!payload.accessToken) {
          throw new Error('refresh accessToken missing');
        }
        localStorage.setItem('uvp_access_token', payload.accessToken);
        if (payload.refreshToken) {
          localStorage.setItem('uvp_refresh_token', payload.refreshToken);
        }
        return payload.accessToken;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
};

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.baseURL = API_BASE_URL;
    const token = localStorage.getItem('uvp_access_token');
    if (token && config.headers) config.headers['Authorization'] = `Bearer ${token}`;
    // 确保所有请求都带有 X-Workspace-Id 头
    const workspaceId = localStorage.getItem('uvp_current_workspace_id') || '1';
    if (config.headers) config.headers['X-Workspace-Id'] = workspaceId;
    return config;
  },
  e => Promise.reject(e)
);

axiosInstance.interceptors.response.use(
  r => unwrapApiPayload(r.data),
  async (error: AxiosError) => {
    const req = error.config as RetriableRequestConfig | undefined;
    if (error.response?.status === 401 && req && !req._retry && shouldAttemptTokenRefresh(req)) {
      req._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        req.headers = req.headers ?? {};
        req.headers['Authorization'] = `Bearer ${accessToken}`;
        return axiosInstance(req);
      } catch (refreshError) {
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }
    const errorPayload = (error.response?.data ?? {}) as ErrorPayload;
    const msg = errorPayload.message || errorPayload.msg || error.message || '网络异常，请稍后重试';
    const wrapped = new Error(msg) as ApiClientError;
    wrapped.status = error.response?.status;
    return Promise.reject(wrapped);
  }
);

const apiClient = {
  request<T = unknown, D = unknown>(config: AxiosRequestConfig<D>) {
    return axiosInstance.request<unknown, T, D>(config);
  },
  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return axiosInstance.get<unknown, T>(url, config);
  },
  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return axiosInstance.delete<unknown, T>(url, config);
  },
  post<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.post<unknown, T, D>(url, data, config);
  },
  put<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.put<unknown, T, D>(url, data, config);
  },
  patch<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.patch<unknown, T, D>(url, data, config);
  },
  defaults: axiosInstance.defaults,
  interceptors: axiosInstance.interceptors,
};

export default apiClient;
